import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useShop, API } from "../context/ShopContext";

const labels = {
  it: {
    title: "Cosa dicono di noi",
    based: (n) => `${n} recensioni`,
    google: "Recensioni Google",
    trustpilot: "Recensioni Trustpilot",
    combined: "Recensioni Google e Trustpilot",
    seeAll: "Vedi tutte",
    readMore: "Leggi tutto",
    readLess: "Riduci",
  },
  en: {
    title: "What our clients say",
    based: (n) => `${n} reviews`,
    google: "Google reviews",
    trustpilot: "Trustpilot reviews",
    combined: "Google & Trustpilot reviews",
    seeAll: "See all",
    readMore: "Read more",
    readLess: "Show less",
  },
};

const TRUSTPILOT_URL = "https://it.trustpilot.com/review/www.ideamedia.org";
const GOOGLE_URL = "https://www.google.com/maps/search/?api=1&query=IdeaMedia%20agenzia%20web%20Padova";

const GoogleIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Google">
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.4 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
  </svg>
);

const TrustpilotIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-label="Trustpilot">
    <path fill="#00B67A" d="M12 0l2.9 8.9H24l-7.4 5.3 2.8 8.9L12 17.7l-7.4 5.4 2.8-8.9L0 8.9h9.1z" />
    <path fill="#005128" d="m12 17.7 3.4-1 1.5 4.9z" />
  </svg>
);

export const Stars = ({ value, size = 15 }) => (
  <span className="idm-stars" aria-label={`${value}/5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i <= value ? "#fbbc04" : "#dadce0"}>
        <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ))}
  </span>
);

const formatDate = (d, lang) => {
  const parts = (d || "").split(/[.-]/);
  if (parts.length !== 3) return d;
  const [y, m, day] = parts;
  return lang === "it" ? `${day}/${m}/${y}` : `${m}/${day}/${y}`;
};

const ReviewCard = ({ review, lang, c }) => {
  const [expanded, setExpanded] = useState(false);
  const long = (review.text || "").length > 200;
  return (
    <article className={`idm-rev-card ${expanded ? "expanded" : ""}`} data-testid="review-card">
      <div className="idm-rev-head">
        {review.avatar ? (
          <img
            src={review.avatar}
            alt={review.name}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        <span className="idm-rev-avatar-fallback" style={{ display: review.avatar ? "none" : "flex" }}>
          {(review.name || "?").charAt(0).toUpperCase()}
        </span>
        <div className="idm-rev-who">
          <strong>{review.name}</strong>
          <span>{formatDate(review.date, lang)}</span>
        </div>
        {review.source === "Trustpilot" ? <TrustpilotIcon size={17} /> : <GoogleIcon size={18} />}
      </div>
      <Stars value={review.rating} />
      {review.text && (
        <p className={`idm-rev-text ${!expanded && long ? "clamped" : ""}`}>{review.text}</p>
      )}
      {long && (
        <button className="idm-rev-more" onClick={() => setExpanded(!expanded)}>
          {expanded ? c.readLess : c.readMore}
        </button>
      )}
    </article>
  );
};

const SourceSummary = ({ icon, label, data, url, c, testid }) => {
  if (!data) return null;
  return (
    <a className="idm-rev-summary" href={url} target="_blank" rel="noreferrer" data-testid={testid}>
      {icon}
      <div>
        <div className="idm-rev-score">
          <strong>{data.rating ? data.rating.toFixed(1) : "-"}</strong>
          <Stars value={Math.round(data.rating || 0)} size={16} />
        </div>
        <p>{label} — {c.based(data.total)}</p>
      </div>
    </a>
  );
};

const Reviews = () => {
  const { lang } = useShop();
  const c = labels[lang];
  const [data, setData] = useState(null);
  const rowRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || !data.reviews?.length) return null;

  const scrollRow = (dir) => rowRef.current?.scrollBy({ left: dir * 350, behavior: "smooth" });
  const sources = data.sources || {};

  // Riepilogo combinato Google + Trustpilot: media ponderata sui rispettivi totali.
  const g = sources.google;
  const tp = sources.trustpilot;
  const combined = (g || tp)
    ? {
        rating: (g && tp)
          ? Math.round(((g.rating * g.total + tp.rating * tp.total) / (g.total + tp.total)) * 10) / 10
          : (g || tp).rating,
        total: (g?.total || 0) + (tp?.total || 0),
      }
    : null;

  return (
    <section className="idm-section idm-section-gray idm-reviews" data-testid="reviews-section">
      <div className="idm-container">
        <h2 className="idm-center" style={{ fontSize: 26, fontWeight: 600, textTransform: "uppercase", letterSpacing: 2 }}>
          {c.title}
        </h2>
        <span className="idm-separator" />

        <div className="idm-rev-summaries">
          {/* Le recensioni arrivano aggregate da Trustindex (Google + Trustpilot):
              un unico riquadro con entrambi i loghi e l'etichetta combinata. */}
          <SourceSummary
            icon={
              <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                <GoogleIcon size={28} />
                <TrustpilotIcon size={28} />
              </span>
            }
            label={c.combined}
            data={combined}
            url={GOOGLE_URL}
            c={c}
            testid="reviews-summary-google"
          />
        </div>

        <div className="idm-rev-slider">
          <button className="idm-rev-arrow" onClick={() => scrollRow(-1)} aria-label="Previous" data-testid="reviews-prev">
            <ChevronLeft size={18} />
          </button>
          <div className="idm-rev-row" ref={rowRef} data-testid="reviews-row">
            {data.reviews.map((r) => (
              <ReviewCard key={`${r.source}-${r.id}`} review={r} lang={lang} c={c} />
            ))}
          </div>
          <button className="idm-rev-arrow" onClick={() => scrollRow(1)} aria-label="Next" data-testid="reviews-next">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
